import { io } from 'socket.io-client';
import { getAnalyticsApiBase } from './analyticsApiBase';
import { devLog } from '../utils/devLog';

const ANALYTICS_API_URL = getAnalyticsApiBase();

// Local dev only: most developers do not run analytics-api-service, and an uncapped
// reconnect loop floods the console for as long as the tab is open. Hosted builds keep
// retrying forever — chat is clinical communication and must recover after an outage.
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '[::1]', '::1'];
const MAX_LOCAL_ATTEMPTS_BEFORE_FIRST_CONNECT = 5;

function isLocalUrl(url) {
  try {
    return LOCAL_HOSTS.indexOf(new URL(url).hostname) !== -1;
  } catch (_) {
    return false;
  }
}

// Server ack error → user-facing message. Ack shapes come from chatHandler.js
// (rate_limited, unauthorized, session_closed, invalid_payload, not_joined,
// internal_error). Kept short — the modal renders it inline.
function mapAckError(code) {
  switch (code) {
    case 'rate_limited': return 'Sending too fast, please wait a moment.';
    case 'unauthorized': return 'You no longer have access to this conversation.';
    case 'session_closed': return 'This conversation has ended.';
    case 'not_joined': return 'Chat is reconnecting — please try again.';
    case 'invalid_payload': return 'Message could not be sent.';
    default: return 'Message could not be delivered.';
  }
}

class ChatSocketService {
  constructor() {
    this.socket = null;
    this.openModals = new Set(); // statusMessageIds with a Chat modal mounted
    this.listeners = new Map();  // event -> Set<fn>

    // Best-effort: notify the server the socket is going away when the
    // browser tab is closing or the page is being unloaded. The standard
    // `beforeunload` event fires synchronously and gives Socket.io a brief
    // window to send a clean DISCONNECT frame. Without this, the server
    // takes up to ~85 s of ping-timeout to notice the dead socket, during
    // which the partner still sees the green "online" dot.
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('beforeunload', () => {
        try { this.disconnect(); } catch (_) { /* noop */ }
      });
    }
  }

  isConnected() {
    return !!(this.socket && this.socket.connected);
  }

  connect(jwt) {
    if (this.socket && this.socket.connected) return this.socket;

    this.socket = io(ANALYTICS_API_URL, {
      auth: { token: jwt },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    // Give up only if a LOCAL server was never reachable. Once connected, a later drop
    // reconnects as before. disconnect() stops socket.io's reconnection manager; opening
    // a chat modal calls connect() again, so chat still retries when someone needs it.
    if (isLocalUrl(ANALYTICS_API_URL)) {
      const socket = this.socket;
      let everConnected = false;
      let failures = 0;
      socket.on('connect', () => { everConnected = true; });
      socket.on('connect_error', () => {
        if (everConnected) return;
        failures += 1;
        if (failures >= MAX_LOCAL_ATTEMPTS_BEFORE_FIRST_CONNECT) {
          devLog(`[chatSocket] ${ANALYTICS_API_URL} unreachable after ${failures} attempts — not retrying. Run analytics-api-service locally to enable chat.`);
          socket.disconnect();
        }
      });
    }

    const fanout = (evt) => (payload) => {
      const subs = this.listeners.get(evt);
      if (subs) subs.forEach((fn) => fn(payload));
    };
    [
      'chat:joined', 'chat:message', 'chat:typing', 'chat:read', 'chat:closed',
      'chat:participant:joined', 'chat:participant:left', 'chat:notify', 'chat:error',
      'chat:loud',
      'presence:update',
      'auth:refresh_required', 'auth:expired',
    ].forEach((evt) => this.socket.on(evt, fanout(evt)));

    // FMEA #2 follow-up — the server emits `auth:refresh_required` 60s before
    // the JWT expires and `auth:expired` at expiry. Surface both through the
    // existing chat:error slot in the chat modal so the user gets a visible
    // signal instead of a silent disconnect. Consumers that want to react
    // more richly (e.g. proactive token refresh + reconnect) can subscribe
    // to the raw events directly via chatSocketService.on('auth:*').
    this.socket.on('auth:refresh_required', () => {
      const subs = this.listeners.get('chat:error');
      if (subs) subs.forEach((fn) => {
        try { fn({ message: 'Your session will end soon — please save any drafts and refresh the page.' }); } catch (_) { /* ignore */ }
      });
    });
    this.socket.on('auth:expired', () => {
      const subs = this.listeners.get('chat:error');
      if (subs) subs.forEach((fn) => {
        try { fn({ message: 'Your session has expired. Please refresh the page to sign back in.' }); } catch (_) { /* ignore */ }
      });
      // Server will disconnect us immediately after emitting; drop the client
      // socket too so reconnect doesn't loop against an expired token.
      try { this.socket && this.socket.disconnect(); } catch (_) { /* ignore */ }
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.openModals.clear();
    this.listeners.clear();
  }

  on(event, fn) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event).add(fn);
    return () => this.listeners.get(event)?.delete(fn);
  }

  joinChat(statusMessageId) {
    if (!this.socket) return;
    this.openModals.add(statusMessageId);
    this.socket.emit('chat:join', { statusMessageId });
  }

  leaveChat(statusMessageId) {
    this.openModals.delete(statusMessageId);
    // No explicit chat:leave event. Staying in the room is harmless until disconnect.
    // Staff-side disconnect happens via disconnect() on modal close.
  }

  sendMessage(statusMessageId, content) {
    if (!this.socket) return null;
    // FMEA #1 — client-generated idempotency key so a retry after a socket
    // drop cannot produce a duplicate message. Server enforces uniqueness on
    // (sessionId, clientMessageId) and returns the previously-persisted row
    // if it sees a duplicate.
    const clientMessageId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `cm_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    // Socket.io ack: the server invokes this callback with
    // { ok, messageId, deduplicated?, error? }. Surface a failed persist as
    // a chat:error the modal can display.
    this.socket.emit('chat:message', { statusMessageId, content, clientMessageId }, (ack) => {
      if (!ack || ack.ok === true) return;
      const handlers = this.listeners.get('chat:error') || [];
      handlers.forEach((fn) => {
        try { fn({ message: mapAckError(ack.error) }); } catch (_) { /* ignore */ }
      });
    });
    return clientMessageId;
  }

  sendTyping(statusMessageId) {
    if (!this.socket) return;
    this.socket.emit('chat:typing', { statusMessageId });
  }

  markRead(statusMessageId) {
    if (!this.socket) return;
    this.socket.emit('chat:read', { statusMessageId });
  }

  closeChat(statusMessageId, addToComms = false) {
    if (!this.socket) return;
    this.socket.emit('chat:close', { statusMessageId, addToComms: !!addToComms });
  }

  isModalOpen(statusMessageId) {
    return this.openModals.has(statusMessageId);
  }

  /**
   * Subscribe to presence updates for a single (role, userId) pair. The
   * callback is invoked with `{ online: boolean }` on subscribe (server replies
   * with current state) and on every transition until unsubscribe.
   *
   * Returns an unsubscribe function.
   */
  subscribePresence(role, userId, callback) {
    if (!this.socket || !role || !userId || typeof callback !== 'function') {
      return () => {};
    }
    const targetUserId = String(userId);

    const filter = (payload) => {
      if (!payload) return;
      if (payload.role !== role) return;
      if (String(payload.userId) !== targetUserId) return;
      callback({ online: !!payload.online });
    };

    const unsubListener = this.on('presence:update', filter);
    this.socket.emit('presence:subscribe', { role, userId: targetUserId }, (resp) => {
      if (resp && typeof resp.online === 'boolean') {
        callback({ online: resp.online });
      }
    });

    return () => {
      try {
        this.socket && this.socket.emit('presence:unsubscribe', { role, userId: targetUserId });
      } catch (_) { /* noop */ }
      unsubListener && unsubListener();
    };
  }
}

const chatSocketService = new ChatSocketService();
export default chatSocketService;
